/* What the guide knows: one entry per screen, and the five steps that take a source from connected
 * to monitored.
 *
 * **Structure here, strings in the catalogue — `navigation.ts`'s shape, and for its reason.** A
 * topic is a group, a title, a sentence and an ordered list of things to do; the ordering and the
 * grouping are what the screen renders, and a flat key→string catalogue cannot hold them. So the
 * structure is typed here and every visible string is an i18n key, exactly as a nav entry carries
 * `labelKey` rather than a label.
 *
 * **The groups are this application's, not the reference's, and that is the one place the reference
 * data is deliberately remapped.** The reference files Remediation, Bulk changes and Autonomous
 * actions under *Governance*; this app's sidebar puts them under *Action*. A guide whose grouping
 * disagrees with the navigation beside it sends a reader to look in the wrong section — which is
 * the opposite of what a guide is for. The group *labels* reuse `nav.section.*`, so the two can
 * never drift apart again.
 *
 * **Three reference topics are absent because their screens are.** Residency, What-if and
 * Configuration have no route in this application — `features/federation/index.ts` records the
 * Residency page being removed in its own words. An entry promising a screen that does not exist
 * would navigate a reader into a 404, and a guide that lies about the product is worse than a guide
 * with 27 entries.
 */

import { SCREEN_PATHS, type ScreenPath } from '@/shared/routes';

/** One screen the guide can explain and open. */
export interface GuideTopic {
  /** Stable id. Used as a React key and as the "you are here" match. */
  readonly id: string;
  /** Where "Open" goes, and what `This page` matches the current location against. */
  readonly path: ScreenPath;
  /** `nav.section.*`, so the guide's grouping is the navigation's grouping. */
  readonly groupKey: string;
  readonly titleKey: string;
  /** One sentence: what the screen is for. */
  readonly whatKey: string;
  /** Ordered. What to do on that screen, rendered numbered. */
  readonly stepKeys: readonly string[];
}

/** One of the five steps on `Start here`. */
export interface StartStep {
  /** Stable id. **This is the localStorage key**, so renaming one silently forgets a reader's
   * progress — which is why it is declared rather than derived from the title. */
  readonly id: string;
  readonly titleKey: string;
  readonly bodyKey: string;
  /** The topic its "Open" button reaches. */
  readonly topicId: string;
}

/* Declaration order is render order, and the grouping is derived from it rather than sorted — so a
 * topic added to the middle of a group appears there instead of alphabetically, and `Home` stays
 * first. */
export const GUIDE_TOPICS: readonly GuideTopic[] = [
  {
    id: 'overview',
    path: SCREEN_PATHS.home,
    groupKey: 'nav.section.home',
    titleKey: 'guide.topic.overview.title',
    whatKey: 'guide.topic.overview.what',
    stepKeys: ['guide.topic.overview.step1', 'guide.topic.overview.step2'],
  },
  {
    id: 'connections',
    path: SCREEN_PATHS.connections,
    groupKey: 'nav.section.data',
    titleKey: 'guide.topic.connections.title',
    whatKey: 'guide.topic.connections.what',
    stepKeys: ['guide.topic.connections.step1', 'guide.topic.connections.step2'],
  },
  {
    id: 'datasets',
    path: SCREEN_PATHS.datasets,
    groupKey: 'nav.section.data',
    titleKey: 'guide.topic.datasets.title',
    whatKey: 'guide.topic.datasets.what',
    stepKeys: [
      'guide.topic.datasets.step1',
      'guide.topic.datasets.step2',
      'guide.topic.datasets.step3',
    ],
  },
  {
    id: 'lineage',
    path: SCREEN_PATHS.lineage,
    groupKey: 'nav.section.data',
    titleKey: 'guide.topic.lineage.title',
    whatKey: 'guide.topic.lineage.what',
    stepKeys: ['guide.topic.lineage.step1'],
  },
  {
    id: 'review',
    path: SCREEN_PATHS.review,
    groupKey: 'nav.section.data',
    titleKey: 'guide.topic.review.title',
    whatKey: 'guide.topic.review.what',
    stepKeys: ['guide.topic.review.step1'],
  },
  {
    id: 'rules',
    path: SCREEN_PATHS.rules,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.rules.title',
    whatKey: 'guide.topic.rules.what',
    stepKeys: [
      'guide.topic.rules.step1',
      'guide.topic.rules.step2',
      'guide.topic.rules.step3',
    ],
  },
  {
    id: 'executions',
    path: SCREEN_PATHS.executions,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.executions.title',
    whatKey: 'guide.topic.executions.what',
    stepKeys: ['guide.topic.executions.step1'],
  },
  {
    id: 'scores',
    path: SCREEN_PATHS.scores,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.scores.title',
    whatKey: 'guide.topic.scores.what',
    stepKeys: ['guide.topic.scores.step1'],
  },
  {
    id: 'anomalies',
    path: SCREEN_PATHS.anomalies,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.anomalies.title',
    whatKey: 'guide.topic.anomalies.what',
    stepKeys: ['guide.topic.anomalies.step1'],
  },
  {
    id: 'incidents',
    path: SCREEN_PATHS.incidents,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.incidents.title',
    whatKey: 'guide.topic.incidents.what',
    stepKeys: ['guide.topic.incidents.step1', 'guide.topic.incidents.step2'],
  },
  {
    id: 'alertRouting',
    path: SCREEN_PATHS.alertRouting,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.alertRouting.title',
    whatKey: 'guide.topic.alertRouting.what',
    stepKeys: ['guide.topic.alertRouting.step1'],
  },
  {
    id: 'reconciliation',
    path: SCREEN_PATHS.reconciliation,
    groupKey: 'nav.section.quality',
    titleKey: 'guide.topic.reconciliation.title',
    whatKey: 'guide.topic.reconciliation.what',
    stepKeys: [
      'guide.topic.reconciliation.step1',
      'guide.topic.reconciliation.step2',
      'guide.topic.reconciliation.step3',
    ],
  },
  {
    id: 'policy',
    path: SCREEN_PATHS.policy,
    groupKey: 'nav.section.governance',
    titleKey: 'guide.topic.policy.title',
    whatKey: 'guide.topic.policy.what',
    stepKeys: ['guide.topic.policy.step1'],
  },
  {
    id: 'compliance',
    path: SCREEN_PATHS.compliance,
    groupKey: 'nav.section.governance',
    titleKey: 'guide.topic.compliance.title',
    whatKey: 'guide.topic.compliance.what',
    stepKeys: ['guide.topic.compliance.step1'],
  },
  {
    id: 'contracts',
    path: SCREEN_PATHS.contracts,
    groupKey: 'nav.section.governance',
    titleKey: 'guide.topic.contracts.title',
    whatKey: 'guide.topic.contracts.what',
    stepKeys: ['guide.topic.contracts.step1'],
  },
  {
    id: 'remediation',
    path: SCREEN_PATHS.remediation,
    groupKey: 'nav.section.action',
    titleKey: 'guide.topic.remediation.title',
    whatKey: 'guide.topic.remediation.what',
    stepKeys: ['guide.topic.remediation.step1'],
  },
  {
    id: 'bulk',
    path: SCREEN_PATHS.bulk,
    groupKey: 'nav.section.action',
    titleKey: 'guide.topic.bulk.title',
    whatKey: 'guide.topic.bulk.what',
    stepKeys: ['guide.topic.bulk.step1'],
  },
  {
    id: 'autonomous',
    path: SCREEN_PATHS.autonomous,
    groupKey: 'nav.section.action',
    titleKey: 'guide.topic.autonomous.title',
    whatKey: 'guide.topic.autonomous.what',
    stepKeys: ['guide.topic.autonomous.step1'],
  },
  {
    id: 'copilot',
    path: SCREEN_PATHS.copilot,
    groupKey: 'nav.section.ai',
    titleKey: 'guide.topic.copilot.title',
    whatKey: 'guide.topic.copilot.what',
    stepKeys: ['guide.topic.copilot.step1', 'guide.topic.copilot.step2'],
  },
  {
    id: 'suggestions',
    path: SCREEN_PATHS.suggestions,
    groupKey: 'nav.section.ai',
    titleKey: 'guide.topic.suggestions.title',
    whatKey: 'guide.topic.suggestions.what',
    stepKeys: ['guide.topic.suggestions.step1'],
  },
  {
    id: 'evals',
    path: SCREEN_PATHS.evals,
    groupKey: 'nav.section.ai',
    titleKey: 'guide.topic.evals.title',
    whatKey: 'guide.topic.evals.what',
    stepKeys: ['guide.topic.evals.step1'],
  },
  {
    id: 'serving',
    path: SCREEN_PATHS.serving,
    groupKey: 'nav.section.ai',
    titleKey: 'guide.topic.serving.title',
    whatKey: 'guide.topic.serving.what',
    stepKeys: ['guide.topic.serving.step1'],
  },
  {
    id: 'marketplace',
    path: SCREEN_PATHS.marketplace,
    groupKey: 'nav.section.platform',
    titleKey: 'guide.topic.marketplace.title',
    whatKey: 'guide.topic.marketplace.what',
    stepKeys: ['guide.topic.marketplace.step1'],
  },
  {
    id: 'metering',
    path: SCREEN_PATHS.metering,
    groupKey: 'nav.section.platform',
    titleKey: 'guide.topic.metering.title',
    whatKey: 'guide.topic.metering.what',
    stepKeys: ['guide.topic.metering.step1'],
  },
  {
    id: 'audit',
    path: SCREEN_PATHS.audit,
    groupKey: 'nav.section.platform',
    titleKey: 'guide.topic.audit.title',
    whatKey: 'guide.topic.audit.what',
    stepKeys: ['guide.topic.audit.step1'],
  },
  {
    id: 'platform',
    path: SCREEN_PATHS.platform,
    groupKey: 'nav.section.platform',
    titleKey: 'guide.topic.platform.title',
    whatKey: 'guide.topic.platform.what',
    stepKeys: ['guide.topic.platform.step1'],
  },
  {
    id: 'admin',
    path: SCREEN_PATHS.admin,
    groupKey: 'nav.section.admin',
    titleKey: 'guide.topic.admin.title',
    whatKey: 'guide.topic.admin.what',
    stepKeys: ['guide.topic.admin.step1'],
  },
];

export const START_STEPS: readonly StartStep[] = [
  {
    id: 'connect',
    titleKey: 'guide.start.connect.title',
    bodyKey: 'guide.start.connect.body',
    topicId: 'connections',
  },
  {
    id: 'profile',
    titleKey: 'guide.start.profile.title',
    bodyKey: 'guide.start.profile.body',
    topicId: 'datasets',
  },
  {
    id: 'rules',
    titleKey: 'guide.start.rules.title',
    bodyKey: 'guide.start.rules.body',
    topicId: 'rules',
  },
  {
    id: 'run',
    titleKey: 'guide.start.run.title',
    bodyKey: 'guide.start.run.body',
    topicId: 'scores',
  },
  {
    id: 'act',
    titleKey: 'guide.start.act.title',
    bodyKey: 'guide.start.act.body',
    topicId: 'incidents',
  },
];

/** The answer for a route this guide has nothing specific to say about.
 *
 * Named rather than `GUIDE_TOPICS[0]`, so reordering the list cannot silently change what an
 * unrecognised route falls back to — and so the fallback is greppable.
 */
const OVERVIEW: GuideTopic = {
  id: 'overview',
  path: SCREEN_PATHS.home,
  groupKey: 'nav.section.home',
  titleKey: 'guide.topic.overview.title',
  whatKey: 'guide.topic.overview.what',
  stepKeys: ['guide.topic.overview.step1', 'guide.topic.overview.step2'],
};

export function topicById(id: string): GuideTopic | undefined {
  return GUIDE_TOPICS.find((topic) => topic.id === id);
}

/** The topic a reader is currently on, or the overview.
 *
 * **Longest match wins, and that is the whole of this function.** `/` is a prefix of every path, and
 * `/datasets` is a prefix of `/datasets/source/:id` — so a first-match scan would answer `Overview`
 * for every screen in the product, which is the failure mode that makes a "This page" tab read as
 * broken while every layer reports success. Falling back to the overview rather than to `undefined`
 * keeps the tab from ever being empty: an unrecognised route is somewhere this guide has nothing
 * specific to say, and the home entry is the honest answer to that.
 */
export function topicForPath(pathname: string): GuideTopic {
  let best: GuideTopic | undefined;
  for (const topic of GUIDE_TOPICS) {
    if (topic.path === '/') {
      if (pathname === '/') best = best ?? topic;
      continue;
    }
    if (!isUnder(pathname, topic.path)) continue;
    if (best === undefined || topic.path.length > best.path.length) best = topic;
  }
  return best ?? OVERVIEW;
}

function isUnder(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Topics grouped for `All topics`, filtered by a search term.
 *
 * Matches title **and** description, because a reader searching "credentials" is describing what
 * they want rather than naming the screen — and `Connections` is only findable by its sentence.
 * Groups with no surviving item are dropped rather than rendered empty.
 */
export function groupedTopics(
  query: string,
  translate: (key: string) => string,
): readonly { readonly groupKey: string; readonly topics: readonly GuideTopic[] }[] {
  const term = query.trim().toLowerCase();
  const groups: { groupKey: string; topics: GuideTopic[] }[] = [];

  for (const topic of GUIDE_TOPICS) {
    if (term !== '') {
      const haystack =
        `${translate(topic.titleKey)} ${translate(topic.whatKey)}`.toLowerCase();
      if (!haystack.includes(term)) continue;
    }
    const existing = groups.find((group) => group.groupKey === topic.groupKey);
    if (existing === undefined) {
      groups.push({ groupKey: topic.groupKey, topics: [topic] });
    } else {
      existing.topics.push(topic);
    }
  }
  return groups;
}
